import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import DashboardTable from "./DashboardTable";
import { useNavigate } from "react-router-dom";
import { Waiting } from "../Waiting";
import { t } from "i18next";
import { useAppContext } from "../../context/AppContext";
import { TestInstanceStatus } from "../../graphql/enums";
import { formatSecondsToString } from "../../utils";
import { format } from "date-fns";
import { getCurrentDateFormat } from "../../App";

export default function DashboardAssignedToMe({
  activeTab,
  tData,
  tLoading,
  refreshMyTestInstances
}) {
  const { actions } = useAppContext();
  const navigate = useNavigate();
  const [candidateToSubmitTableData, setCandidateToSubmitTableData] = useState(
    []
  );

  const [currentDatetime, setCurrentDatetime] = useState(Date.now());
  let dateFormat = getCurrentDateFormat();
  

  const candidateToSubmitTableHeaders = [
    { key: "name", value: t("TestName"), sortable: true },
    { key: "assignedBy", value: t("AssignedBy"), sortable: true },
    { key: "status", value: t("Status"), sortable: false },
    { key: "dueDate", value: t("DueDate"), sortable: true },
    { key: "timeRemaining", value: t("TimeRemaining"), sortable: false },
  ];

  const handleRefreshMyTestInstances = () => {
    refreshMyTestInstances();
  };

  const handleRowActionClick = (action, row) => {
    switch (action) {
      case "resume":
        handleResumeTest(row.instance);
        break;
      case "reserve":
        handleMakeReservation(row.instance);
        break;
      case "cancel":
        handleCancelReservation(row.instance);
        break;
      case "submit":
        handleSubmitTest(row.instance);
        break;
      case "accept":
        handleAcceptTest(row.instance);
        break;
      case "decline":
        handleDeclineTest(row.instance);
        break;
      case "forfeit":
        handleForfeitTest(row.instance);
        break;
      case "discard":
        handleDiscardTest(row.instance);
        break;
      case "revoke":
        handleRevokeTest(row.instance);
        break;
      default:
        console.log(action, row);
    }
  };

  useEffect(() =>{
    var timerId = setInterval(() => setCurrentDatetime(Date.now()), 1000);
    return () => clearInterval(timerId);
  });

  // map the received data to table row friendly data for both tables
  useEffect(() => {
    let testAvailable = {};
    let allowCancel = false;

    if (tData?.getTestInstancesAssignedToMe) {
      const testInstances = tData.getTestInstancesAssignedToMe;
      const toSubmit = [];

      const CandidateTestInstanceStatusText = {
        [TestInstanceStatus.Active]: t("InProgress"),
        [TestInstanceStatus.Suspended]: t("DeadlinePassed"),
        [TestInstanceStatus.Assigned]: t("NotYetStarted"),
        [TestInstanceStatus.Marked]: t("Evaluated"),
        [TestInstanceStatus.Unmarked]: t("PendingEvaluation"),
      };

      const getCandidateTestInstanceActionsPerStatus = (
        testInstance,
        isDeadlineReached
      ) => {
        let instanceId = testInstance.id;
        switch (testInstance.status) {
          case TestInstanceStatus.Created:
            return [
              {
                key: "accept",
                value: t("Accept"),
                disabled: isDeadlineReached,
              },
              { key: "decline", value: t("Decline") },
            ];
          case TestInstanceStatus.Assigned:
            return [
              {
                key: "accept",
                value: t("Accept"),
                disabled: isDeadlineReached,
              },
              { key: "decline", value: t("Decline") },
            ];
          case TestInstanceStatus.Active: {
            let testActions = [];
            if (testAvailable[instanceId]) {
              testActions.push({
                key: testAvailable[instanceId].key,
                value: t(testAvailable[instanceId].value),
              });
            }
            if (allowCancel) {
              testActions.push({
                key: "cancel",
                value: t("CancelReservation"),
              });
            }
            if (testInstance.workTimeRemaining < testInstance.testTemplate.maxWorkTime) {
              testActions.push({ key: "submit", value: t("Submit") });
            }
            testActions.push({ key: "forfeit", value: t("Forfeit") });
            return testActions;
          }
          case TestInstanceStatus.Suspended:
            return [
              { key: "submit", value: t("Submit") },
              { key: "discard", value: t("Discard") },
            ];
          case TestInstanceStatus.Marked:
            return [
              { key: "viewEvaluation", value: t("ViewEvaluation") }
            ];
          case TestInstanceStatus.UnmarkedComplete:
              return [];
          case TestInstanceStatus.Unmarked:
            return [];
          case TestInstanceStatus.Cancelled:
            return [];

          default:
            break;
        }
      };

      testInstances.forEach((testInstance) => {
        const deadlineDatetime = new Date(
          testInstance.finalTargetCompletionDate
        ).getTime();

        const currentReservation = testInstance.reservations?.find((r) => {
          let now = Date.now();
          let start = new Date(r.timeslot.start).getTime();
          return start + r.timeslot.duration * 1000 > now;
        });

        const currentReservationTime = new Date(currentReservation?.timeslot.start).getTime();

        // accept action is disabled when the deadline has passed or the test doesn't have work time left.
        const isDeadlineReached =
          currentDatetime > deadlineDatetime ||
          testInstance.workTimeRemaining === 0;

        // resume action is enabled only when the test has an active reservation
        const currentReservationDurationWithGrace = currentReservation ? 
          currentReservation.timeslot.duration + 60 * 5 : 0; 

        if (currentDatetime < deadlineDatetime &&
          testInstance.workTimeRemaining < testInstance.testTemplate.maxWorkTime &&
          ((currentReservation && currentReservationTime <= currentDatetime &&
            currentReservationTime + currentReservationDurationWithGrace * 1000 >
            currentDatetime) ||
          !testInstance.testTemplate.requiresReservation)
        ) {
          testAvailable[testInstance.id] = {
            key: "resume",
            value: "Resume",
          };
          if (currentReservationTime <= currentDatetime && 
              currentReservationTime + currentReservationDurationWithGrace * 1000 > currentDatetime) {
            allowCancel = true;
          }
        } else if (
          currentDatetime < deadlineDatetime &&
          testInstance.workTimeRemaining === testInstance.testTemplate.maxWorkTime &&
          ((currentReservation && currentReservationTime <= currentDatetime &&
            currentReservationTime + currentReservationDurationWithGrace * 1000 >
            currentDatetime) ||
          !testInstance.testTemplate.requiresReservation)
        ) {
          testAvailable[testInstance.id] = {
            key: "resume",
            value: "StartTest",
          };
          if (currentReservationTime <= currentDatetime && 
            currentReservationTime + currentReservationDurationWithGrace * 1000 > currentDatetime) {
            allowCancel = true;
          }
        } else if (
          currentDatetime < deadlineDatetime &&
          currentReservationTime > currentDatetime &&
          testInstance.workTimeRemaining > 0
        ) {
          testAvailable[testInstance.id] = {
            key: "",
            value:
              t("UpcomingReservation") +
              " " +
              format(new Date(currentReservation.timeslot.start), "PPp", {
                locale: dateFormat,
              }),
          };
          allowCancel = true;
        } else if (
          !currentReservation &&
          currentDatetime < deadlineDatetime &&
          testInstance.workTimeRemaining > 0
        ) {
          testAvailable[testInstance.id] = {
            key: "reserve",
            value: "ReserveTime",
          };
          allowCancel = false;
        }

        const actions = getCandidateTestInstanceActionsPerStatus(
          testInstance,
          isDeadlineReached
        );

        // To submit table
        if (
          [
            TestInstanceStatus.Assigned,
            TestInstanceStatus.Active,
            TestInstanceStatus.Suspended,
            TestInstanceStatus.Created,
          ].includes(testInstance.status)
        ) {

          const testTableRow = {
            id: testInstance.id,
            fields: [
              { key: "name", value: testInstance.testTemplate.name },
              {
                key: "assignedBy",
                value:
                  testInstance.assignedBy.firstname +
                  " " +
                  testInstance.assignedBy.lastname,
              },
              {
                key: "status",
                value: CandidateTestInstanceStatusText[testInstance.status],
              },
              {
                key: "dueDate",
                value: format(
                  new Date(testInstance.finalTargetCompletionDate),
                  "PPP",
                  { locale: dateFormat }
                ),
              },
              {
                key: "timeRemaining",
                value: formatSecondsToString(testInstance.workTimeRemaining),
              },
            ],
            actions,
            instance: testInstance,
          };
          toSubmit.push(testTableRow);
        }
      });
      // refreshMyTestInstances();
      setCandidateToSubmitTableData(toSubmit);
    }
  }, [tData, dateFormat, refreshMyTestInstances, currentDatetime]);

  const handleAcceptTest = (testInstance) => {
    // Don't allow beginning of the test if no time is left
    const deadlineDatetime = new Date(testInstance.finalTargetCompletionDate);
    const currentDatetime = new Date();
    if (
      currentDatetime > deadlineDatetime ||
      testInstance.workTimeRemaining === 0
    )
      return;
    actions.setCurrentTestInstance(testInstance);
    navigate("/app/testTermsAndConditions");
  };

  const handleDeclineTest = (testInstance) => {
    actions.setCurrentTestInstance(testInstance);
    navigate("/app/declineTest");
  };

  const handleForfeitTest = (testInstance) => {
    actions.setCurrentTestInstance(testInstance);
    navigate("/app/forfeitTest");
  };

  const handleDiscardTest = (testInstance) => {
    actions.setCurrentTestInstance(testInstance);
    navigate("/app/discardTest");
  };

  const handleRevokeTest = (testInstance) => {
    actions.setCurrentTestInstance(testInstance);
    navigate("/app/revokeTest");
  };

  const handleResumeTest = (testInstance) => {
    actions.setCurrentTestInstance(testInstance);
    navigate("/app/testinstance");
  };

  const handleMakeReservation = (testInstance) => {
    actions.setCurrentTestInstance(testInstance);
    navigate("/app/makeReservation");
  };

  const handleCancelReservation = (testInstance) => {
    actions.setCurrentTestInstance(testInstance);
    navigate("/app/cancelReservation");
  };

  const handleSubmitTest = (testInstance) => {
    actions.setCurrentTestInstance(testInstance);
    navigate("/app/submitTest");
  };

  let pageContents;
  if (tLoading) {
    pageContents = <Waiting />;
  } else if (tData) {
    pageContents = (
      <div
        role="tabpanel"
        hidden={activeTab !== 3}
        id="tabpanel-3"
        aria-labelledby="tab-3"
      >
        <Box>
          <DashboardTable
            title={t("CandidateTableHeader")}
            headers={candidateToSubmitTableHeaders}
            data={candidateToSubmitTableData}
            onRowActionClick={handleRowActionClick}
            isLoading={tLoading}
            emptyText={t("NoTestsMessage")}
            onRefresh={handleRefreshMyTestInstances}
            isCollapsible={true}
          />
        </Box>
      </div>
    );
  } else {
    pageContents = "";
  }

  return pageContents;
}
